import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  FileText, Shield, Building, DollarSign, Calendar, Download,
  Printer, ChevronDown, Eye, Loader2, ArrowLeft, X
} from 'lucide-react';
import { fmtCurrency } from '../lib/localeUtils';
import { exportToPrintablePDF } from '../lib/exportUtils';

function exportCSV(filename, headers, rows) {
  const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function BillingPage() {
  const [insuranceSummary, setInsuranceSummary] = useState(null);
  const [corporateSummary, setCorporateSummary] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);

  // Detail view state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailType, setDetailType] = useState(''); // 'insurance' | 'corporate'
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom + 'T00:00:00';
      if (dateTo) params.date_to = dateTo + 'T23:59:59';
      const [insRes, corpRes] = await Promise.all([
        api.get('/billing/insurance-summary', { params }),
        api.get('/billing/corporate-summary', { params }),
      ]);
      setInsuranceSummary(insRes.data);
      setCorporateSummary(corpRes.data);
    } catch (e) { console.error(e); toast.error('Failed to load billing data'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchBilling(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = () => fetchBilling();

  const grandTotal = (insuranceSummary?.grand_total_billed || 0) + (corporateSummary?.grand_total_billed || 0);

  // Drill into individual provider/client
  const viewInsuranceDetail = async (providerId) => {
    setDetailLoading(true);
    setDetailType('insurance');
    setDetailOpen(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom + 'T00:00:00';
      if (dateTo) params.date_to = dateTo + 'T23:59:59';
      const res = await api.get(`/insurance-providers/${providerId}/billing`, { params });
      setDetailData(res.data);
    } catch { toast.error('Failed to load provider details'); }
    finally { setDetailLoading(false); }
  };

  const viewCorporateDetail = async (clientId) => {
    setDetailLoading(true);
    setDetailType('corporate');
    setDetailOpen(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom + 'T00:00:00';
      if (dateTo) params.date_to = dateTo + 'T23:59:59';
      const res = await api.get(`/corporate-clients/${clientId}/billing`, { params });
      setDetailData(res.data);
    } catch { toast.error('Failed to load client details'); }
    finally { setDetailLoading(false); }
  };

  // Export helpers
  const exportOverviewPDF = () => {
    const summaryCards = [
      { label: 'Grand Total', value: fmtCurrency(grandTotal) },
      { label: 'Insurance', value: fmtCurrency(insuranceSummary?.grand_total_billed || 0) },
      { label: 'Corporate', value: fmtCurrency(corporateSummary?.grand_total_billed || 0) },
    ];
    const headers = ['Type', 'Name', 'Sales', 'Total Billed'];
    const rows = [
      ...(insuranceSummary?.providers || []).map(p => ['Insurance', p.provider_name, p.total_sales, fmtCurrency(p.total_billed)]),
      ...(corporateSummary?.clients || []).map(c => ['Corporate', c.client_name, c.total_sales, fmtCurrency(c.total_billed)]),
    ];
    exportToPrintablePDF({
      title: 'Billing Overview Report',
      subtitle: dateFrom || dateTo ? `Period: ${dateFrom || '...'} to ${dateTo || '...'}` : 'All Time',
      summaryCards, headers, rows,
    });
  };

  const exportOverviewCSV = () => {
    const headers = ['Type', 'Name', 'Sales', 'Total Billed', 'Claims/Discounts', 'Clients Served'];
    const rows = [
      ...(insuranceSummary?.providers || []).map(p => ['Insurance', p.provider_name, p.total_sales, p.total_billed, p.total_claims, p.clients_served]),
      ...(corporateSummary?.clients || []).map(c => ['Corporate', c.client_name, c.total_sales, c.total_billed, c.total_discount, c.unique_customers]),
    ];
    exportCSV(`billing_overview_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const exportDetailPDF = () => {
    if (!detailData) return;
    const isIns = detailType === 'insurance';
    const name = isIns ? detailData.provider?.name : detailData.client?.name;
    const summary = detailData.billing_summary || {};
    const summaryCards = [
      { label: 'Total Sales', value: summary.total_sales },
      { label: 'Total Billed', value: fmtCurrency(summary.total_billed) },
      ...(isIns
        ? [{ label: 'Claims', value: fmtCurrency(summary.total_claims) }, { label: 'Copay', value: fmtCurrency(summary.total_copay) }]
        : [{ label: 'Discounts', value: fmtCurrency(summary.total_discount) }, { label: 'Employees', value: summary.employees_served }]),
    ];
    const headers = ['Date', 'Sale ID', 'Customer', 'Items', 'Total', 'Payment'];
    const rows = (detailData.sales || []).map(s => [
      (s.created_at || '').slice(0, 10), s.sale_id || '',
      s.customer_name || 'Walk-in', s.items?.length || 0,
      fmtCurrency(s.total), s.payment_method || '',
    ]);
    exportToPrintablePDF({
      title: `${isIns ? 'Insurance' : 'Corporate'} Billing Report`,
      subtitle: `${name} ${dateFrom || dateTo ? `| ${dateFrom || '...'} to ${dateTo || '...'}` : '| All Time'}`,
      summaryCards, headers, rows,
    });
  };

  const exportDetailCSV = () => {
    if (!detailData) return;
    const isIns = detailType === 'insurance';
    const name = isIns ? detailData.provider?.name : detailData.client?.name;
    const headers = ['Date', 'Sale ID', 'Customer', 'Items', 'Total', 'Payment Method'];
    const rows = (detailData.sales || []).map(s => [
      (s.created_at || '').slice(0, 10), s.sale_id || '',
      s.customer_name || 'Walk-in', s.items?.length || 0,
      s.total || 0, s.payment_method || '',
    ]);
    exportCSV(`${(name || 'report').replace(/\s+/g, '_')}_billing_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><FileText className="h-6 w-6 text-primary" /> Billing Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Click any provider or client row to view their individual report</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1.5" data-testid="billing-export-btn">
                <Download className="h-4 w-4" /> Export <ChevronDown className="h-3.5 w-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportOverviewPDF} data-testid="billing-export-pdf">
                <FileText className="mr-2 h-4 w-4" /> Export PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportOverviewCSV} data-testid="billing-export-csv">
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" className="gap-1.5" onClick={exportOverviewPDF} data-testid="billing-print-btn">
            <Printer className="h-4 w-4" /> Print
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" /> From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="billing-date-from" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="billing-date-to" />
            </div>
            <Button onClick={handleFilter} variant="outline" data-testid="billing-filter-btn">Apply Filter</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><DollarSign className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-xs text-muted-foreground">Grand Total Billed</p>
                <p className="text-2xl font-bold" data-testid="grand-total">{fmtCurrency(grandTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center"><Shield className="h-5 w-5 text-blue-500" /></div>
              <div>
                <p className="text-xs text-muted-foreground">Insurance Billed</p>
                <p className="text-2xl font-bold text-blue-500" data-testid="insurance-total">{fmtCurrency((insuranceSummary?.grand_total_billed || 0))}</p>
                <p className="text-xs text-muted-foreground">{insuranceSummary?.total_providers || 0} providers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center"><Building className="h-5 w-5 text-orange-500" /></div>
              <div>
                <p className="text-xs text-muted-foreground">Corporate Billed</p>
                <p className="text-2xl font-bold text-orange-500" data-testid="corporate-total">{fmtCurrency((corporateSummary?.grand_total_billed || 0))}</p>
                <p className="text-xs text-muted-foreground">{corporateSummary?.total_clients || 0} clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Insurance / Corporate */}
      <Tabs defaultValue="insurance">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="insurance" className="gap-1.5" data-testid="tab-insurance"><Shield className="h-3.5 w-3.5" /> Insurance Partners</TabsTrigger>
          <TabsTrigger value="corporate" className="gap-1.5" data-testid="tab-corporate"><Building className="h-3.5 w-3.5" /> Corporate Clients</TabsTrigger>
        </TabsList>

        <TabsContent value="insurance" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Insurance Partner Billing</CardTitle>
              <p className="text-xs text-muted-foreground">Click a row to generate individual report</p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead className="text-right">Sales</TableHead>
                    <TableHead className="text-right">Total Billed</TableHead>
                    <TableHead className="text-right">Claims</TableHead>
                    <TableHead className="text-right">Copay</TableHead>
                    <TableHead className="text-right">Clients</TableHead>
                    <TableHead className="text-right w-[80px]">Report</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(insuranceSummary?.providers || []).map(p => (
                    <TableRow key={p.provider_id} className="cursor-pointer hover:bg-muted/50" onClick={() => viewInsuranceDetail(p.provider_id)} data-testid={`ins-row-${p.provider_id}`}>
                      <TableCell className="font-medium">{p.provider_name}</TableCell>
                      <TableCell className="text-right">{p.total_sales}</TableCell>
                      <TableCell className="text-right font-semibold">{fmtCurrency(p.total_billed)}</TableCell>
                      <TableCell className="text-right text-blue-500">{fmtCurrency(p.total_claims)}</TableCell>
                      <TableCell className="text-right">{fmtCurrency(p.total_copay)}</TableCell>
                      <TableCell className="text-right">{p.clients_served}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); viewInsuranceDetail(p.provider_id); }}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!insuranceSummary?.providers || insuranceSummary.providers.length === 0) && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No insurance billing data yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="corporate" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Corporate Client Billing</CardTitle>
              <p className="text-xs text-muted-foreground">Click a row to generate individual report</p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Sales</TableHead>
                    <TableHead className="text-right">Total Billed</TableHead>
                    <TableHead className="text-right">Discounts</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead className="text-right">Customers</TableHead>
                    <TableHead className="text-right w-[80px]">Report</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(corporateSummary?.clients || []).map(c => (
                    <TableRow key={c.client_id} className="cursor-pointer hover:bg-muted/50" onClick={() => viewCorporateDetail(c.client_id)} data-testid={`corp-row-${c.client_id}`}>
                      <TableCell className="font-medium">{c.client_name}</TableCell>
                      <TableCell className="text-right">{c.total_sales}</TableCell>
                      <TableCell className="text-right font-semibold">{fmtCurrency(c.total_billed)}</TableCell>
                      <TableCell className="text-right text-orange-500">{fmtCurrency(c.total_discount)}</TableCell>
                      <TableCell className="text-right">{c.employees_served}</TableCell>
                      <TableCell className="text-right">{c.unique_customers}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); viewCorporateDetail(c.client_id); }}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!corporateSummary?.clients || corporateSummary.clients.length === 0) && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No corporate billing data yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Individual Report Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh]" data-testid="billing-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detailType === 'insurance' ? <Shield className="h-5 w-5 text-blue-500" /> : <Building className="h-5 w-5 text-orange-500" />}
              {detailType === 'insurance'
                ? `${detailData?.provider?.name || 'Insurance'} — Billing Report`
                : `${detailData?.client?.name || 'Corporate'} — Billing Report`}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : detailData ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-[10px] text-muted-foreground uppercase">Sales</p>
                  <p className="text-lg font-bold">{detailData.billing_summary?.total_sales || 0}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-[10px] text-muted-foreground uppercase">Total Billed</p>
                  <p className="text-lg font-bold text-primary">{fmtCurrency(detailData.billing_summary?.total_billed || 0)}</p>
                </div>
                {detailType === 'insurance' ? (
                  <>
                    <div className="p-3 bg-muted/50 rounded-lg text-center">
                      <p className="text-[10px] text-muted-foreground uppercase">Claims</p>
                      <p className="text-lg font-bold text-blue-500">{fmtCurrency(detailData.billing_summary?.total_claims || 0)}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg text-center">
                      <p className="text-[10px] text-muted-foreground uppercase">Copay</p>
                      <p className="text-lg font-bold">{fmtCurrency(detailData.billing_summary?.total_copay || 0)}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-3 bg-muted/50 rounded-lg text-center">
                      <p className="text-[10px] text-muted-foreground uppercase">Discounts</p>
                      <p className="text-lg font-bold text-orange-500">{fmtCurrency(detailData.billing_summary?.total_discount || 0)}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg text-center">
                      <p className="text-[10px] text-muted-foreground uppercase">Employees</p>
                      <p className="text-lg font-bold">{detailData.billing_summary?.employees_served || 0}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Sales Table */}
              <ScrollArea className="max-h-[350px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Sale ID</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Payment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailData.sales || []).length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No sales in this period</TableCell></TableRow>
                    ) : (detailData.sales || []).map(s => (
                      <TableRow key={s.sale_id}>
                        <TableCell className="text-xs">{(s.created_at || '').slice(0, 10)}</TableCell>
                        <TableCell className="text-xs font-mono">{(s.sale_id || '').slice(0, 16)}</TableCell>
                        <TableCell className="text-sm">{s.customer_name || 'Walk-in'}</TableCell>
                        <TableCell className="text-right text-sm">{s.items?.length || 0}</TableCell>
                        <TableCell className="text-right font-semibold text-sm">{fmtCurrency(s.total)}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[9px] capitalize">{(s.payment_method || '').replace(/_/g, ' ')}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              {/* Export Buttons */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-1.5" data-testid="detail-export-btn">
                      <Download className="h-4 w-4" /> Export <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportDetailPDF} data-testid="detail-export-pdf">
                      <FileText className="mr-2 h-4 w-4" /> Export PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportDetailCSV} data-testid="detail-export-csv">
                      <Download className="mr-2 h-4 w-4" /> Export CSV
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" className="gap-1.5" onClick={exportDetailPDF} data-testid="detail-print-btn">
                  <Printer className="h-4 w-4" /> Print
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
